import {
  AnyRegularField,
  FieldState,
  FilledContentRelationshipField,
  FilledLinkToMediaField,
  FilledLinkToWebField,
  GroupField,
  LinkField,
  SelectField,
  RichTextField,
  SliceZone,
} from '@prismicio/client';

export type IPrismicDataInterface = Record<
  string,
  AnyRegularField | GroupField | SliceZone
>;

export interface IPrismicRichTextSpan {
  start: number;
  end: number;
  type: string;
}

export type IPrismicRichText = RichTextField;

interface IPrismicImageDimensions {
  width: number;
  height: number;
}

// @todo: migrate to ImageField, the official Prismic library type
export interface IPrismicImage {
  dimensions: IPrismicImageDimensions;
  url: string;
  alt: string;
}

export interface IPrismicDocument {
  uid?: string;
  type: string;
  link_type: 'Document';
  id: string;
  tags: string[];
  slug: string;
  lang: string;
  isBroken: boolean;
}

export interface IPrismicWebLink {
  id?: string;
  target?: string;
  url: string;
  link_type: 'Web';
}

export interface IPrismicEmptyLink {
  link_type: 'Any';
  id: undefined;
}

export type IPrismicLink<
  TypeEnum = string,
  LangEnum = string,
  DataInterface extends
    | Record<string, AnyRegularField | GroupField | SliceZone>
    | unknown = unknown,
  State extends FieldState = FieldState,
> = LinkField<TypeEnum, LangEnum, DataInterface, State>;

export type FilledPrismicLink<
  TypeEnum = string,
  LangEnum = string,
  DataInterface extends
    | Record<string, AnyRegularField | GroupField | SliceZone>
    | unknown = unknown,
> = LinkField<TypeEnum, LangEnum, DataInterface, 'filled'>;

export type IPrismicCollection = {
  url: string;
  id: number;
  title: string;
  handle: string;
  admin_graphql_api_id: string;
  icon?: string;
  color?: string;
};

export interface IPrismicCustomTypeContent<T, U> {
  id: string;
  slice_type: string;
  items: T;
  primary: U;
}

/**
 * Prefix every key of a type with `ab_test_`
 */
export type IPrismicABTestEnhancer<T> = {
  [K in keyof T as `ab_test_${Extract<K, string>}`]?: T[K];
};

export type IPrismicABTestConfiguration = {
  ab_test_enabled: boolean;
  optimize_id: string;
  preprod_optimize_id: string;
  ab_test_timeout: number;
  feature_flag_key: string | null;
};

/**
 * Adds same keys with `ab_test_` prefixed + some AB test configuration keys
 */
export type IPrismicABTest<T> = T &
  IPrismicABTestEnhancer<T> &
  IPrismicABTestConfiguration;

export const hasABTestConf = (
  content: Record<string, unknown>,
): content is IPrismicABTestConfiguration => {
  return 'ab_test_enabled' in content;
};

export enum AuthContext {
  AUTH_ONLY = 'Authenticated Only',
  ANON_ONLY = 'Anonymous Only',
  MEMBER_ONLY = 'Member Only',
  NOT_MEMBER_ONLY = 'Not Member Only',
  ALL = 'All',
}

export enum AuthContextWithOrder {
  ANONYMOUS = 'Anonymous',
  CONNECTED_BUT_NOT_MEMBER = 'Connected But Not Member',
  CONNECTED_AND_MEMBER_WITH_NO_ORDER = 'Connected And Member With No Order',
  CONNECTED_AND_NOT_MEMBER_WITH_ORDERS = 'Connected And Not Member With Orders',
  CONNECTED_AND_MEMBER_WITH_ORDERS = 'Connected And Member With Orders',
}

export enum PrismicLinkContext {
  BLOG = 'blog',
  SHOP = 'shop',
  ANY = 'any',
}

export type IPrismicLinkDestination = 'blog' | 'shop' | 'support';

export interface IPrismicCartSharedContentsItem {
  auth_context: string;
  widget_id_a: string;
  widget_id_b?: string;
  ab_test_id?: string;
  tracking_id: string;
}

export interface IPrismicCartSharedContents {
  primary: {
    empty_cart_with_zero_order_text: IPrismicRichText;
    empty_cart_with_orders_text: IPrismicRichText;
    side_cart_recos_title: IPrismicRichText;
    no_recos_found_image: IPrismicImage;
  };
  items: IPrismicCartSharedContentsItem[];
}

export interface ICartSharedContentsItem {
  authContext: string;
  widgetIdA: string;
  widgetIdB: string;
  abTestId: string;
  trackingId: string;
}

export interface ICartSharedContents {
  primary: {
    emptyCartWithZeroOrderText: IPrismicRichText;
    emptyCartWithOrdersText: IPrismicRichText;
    sideCartRecosTitle: IPrismicRichText;
    noRecosFoundImage: IPrismicImage;
  };
  items: ICartSharedContentsItem[];
}

export interface ICartSharedSubscriptionTimelineItem {
  stepIcon: IPrismicImage;
  stepTitle: IPrismicRichText;
  stepDescription: IPrismicRichText;
}

export interface ICartSharedSubscriptionTimeline {
  primary: {
    title: string;
    ctaConfirmation: string;
  };
  items: ICartSharedSubscriptionTimelineItem[];
  id: string;
  slice_type: string;
  slice_label: any;
}

export interface IPrismicImageBanner {
  web_banner: IPrismicImage;
  mobile_banner: IPrismicImage;
  link: IPrismicLink;
}

export interface IInformationsModal {
  image: IPrismicImage;
  title: string;
  text: IPrismicRichText;
  button_label: string;
}

export type IPrismicPreview = {
  token: string;
  documentId: string;
  prismicUrl?: string;
  prismicRepository?: string;
};

/** The body type is defined in projects */
export interface IPrismicProductEligibilityModal {
  informative_box_text: IPrismicRichText;
  informative_box_picto: IPrismicImage;
  informative_box_background_color: string;
  modal_title: string;
  eligible_user_informative_box_text: IPrismicRichText;
  eligible_user_informative_box_picto: IPrismicImage;
  eligible_user_informative_box_background_color: string;
  eligible_informative_box_text: IPrismicRichText;
  eligible_informative_box_picto: IPrismicImage;
  eligible_informative_box_background_color: string;
  eligible_text: IPrismicRichText;
  go_to_shelf_cta_label: string;
  go_to_shelf_link: IPrismicLink;
  go_to_cart_cta_label: string;
  not_eligible_informative_box_text: IPrismicRichText;
  not_eligible_informative_box_picto: IPrismicImage;
  not_eligible_informative_box_background_color: string;
  not_eligible_extra_infos: string;
  close_modal_cta_label: string;
  start_again_cta_label: string;
  delete_uneligible_products_cta_label: string;
}

/**
 * Redeclared here because Prismic's lib does not export them 🙃
 */
type OptionalLinkProperties<Variant = string> = {
  text?: string;
  variant?: Variant;
};

export type FilledLinkField =
  | FilledLinkToWebField
  | FilledLinkToMediaField
  | (FilledContentRelationshipField & OptionalLinkProperties);

export type PrismicTextAlignmentField = SelectField<'left' | 'center'>;

export type PrismicTextCasingField = SelectField<'default' | 'uppercase'>;

export type PrismicItemTitleStyleField = SelectField<'subtitle' | 'title'>;

export type PrismicTitleStyleField = SelectField<
  'Display' | 'XL' | 'L' | 'M' | 'S' | 'Subtitle'
>;

export type PrismicContentSerializer = (text: string) => string;

export type PrismicCollectionIntegrationField = {
  breadcrumb: { handle: string; omsId: string; title: string }[];
  color: string | null;
  handle: string;
  icon: string;
  id: number;
  products_count: number;
  title: string;
};
